import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ClerkAuthService } from './clerk-auth.service';
import { IClerkAuthState, IClerkUser } from '../models/auth.models';

@Injectable({ providedIn: 'root' })
export class ClerkAuthStore {
  private clerkAuthenticationFailurePromise: Promise<void> | null = null;
  private clerkTokenRefreshPromise: Promise<string | null> | null = null;

  private readonly clerkAuthService = inject(ClerkAuthService);
  private readonly http = inject(HttpClient);

  private readonly state = signal<IClerkAuthState>({
    clerkUser: null
  });

  readonly clerkUser = computed(() => this.state().clerkUser);
  readonly isAuthenticated = computed(() => Boolean(this.state().clerkUser));
  readonly isImpersonating = computed(() => this.clerkAuthService.isActorSession());
  readonly hasReturnTicket = computed(() => this.clerkAuthService.hasReturnTicket());

  async impersonate(ticket: string): Promise<void> {
    await this.clerkAuthService.signInWithTicket(ticket);
    await this.hydrateFromClerkSdk();
  }

  async returnFromImpersonation(): Promise<boolean> {
    const restored = await this.clerkAuthService.returnFromImpersonation();
    if (restored) {
      await this.hydrateFromClerkSdk();
    } else {
      this.clearSession();
    }
    return restored;
  }

  async restoreSession(): Promise<boolean> {
    return this.clerkAuthService.restoreSession();
  }

  async logout(): Promise<void> {
    try {
      await this.clerkAuthService.signOut();
    } catch {
    } finally {
    }

    this.clearSession();
  }

  async handleClerkAuthenticationFailure(
    message = 'Your session expired. Please sign in again.'
  ): Promise<void> {
    if (this.clerkAuthenticationFailurePromise) {
      return this.clerkAuthenticationFailurePromise;
    }

    this.clearSession();

    this.clerkAuthenticationFailurePromise = this.clerkAuthService.resetAndRedirectToSignIn()
      .catch(() => undefined)
      .finally(() => {
        this.clerkAuthenticationFailurePromise = null;
      });

    return this.clerkAuthenticationFailurePromise;
  }

  async getAuthorizationToken(options?: { forceRefresh?: boolean }): Promise<string | null> {
    if (!options?.forceRefresh) {
      return this.clerkAuthService.getToken();
    }

    if (!this.clerkTokenRefreshPromise) {
      this.clerkTokenRefreshPromise = this.clerkAuthService.getToken({ skipCache: true })
        .finally(() => {
          this.clerkTokenRefreshPromise = null;
        });
    }

    return this.clerkTokenRefreshPromise;
  }

  async hydrateFromClerkSdk(): Promise<boolean> {
    const snapshot = await this.clerkAuthService.getClerkUserSnapshot();
    if (!snapshot) {
      return false;
    }

    this.state.set({ clerkUser: snapshot });
    return true;
  }

  setClerkUser(clerkUser: IClerkUser | null): void {
    this.state.set({ clerkUser });
  }

  clearSession(): void {
    this.state.set({ clerkUser: null });
  }
}